import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Info, Loader2, Lock, Plus, Unlock } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PageContainer, PageHeader, Card, Pill } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency } from "@/hooks/useCurrency";
import api from "@/lib/api";

function formatOpenedAt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function CountersPage() {
  const { t } = useTranslation();
  const { format, step: moneyStep } = useCurrency();
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const branchName = user?.default_branch?.name || "selected branch";

  const [openTarget, setOpenTarget] = useState(null);
  const [closeTarget, setCloseTarget] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [openingFloat, setOpeningFloat] = useState("0");
  const [closingCash, setClosingCash] = useState("");
  const [closeNotes, setCloseNotes] = useState("");
  const [newName, setNewName] = useState("");
  const [newDeviceCode, setNewDeviceCode] = useState("");
  const [newCounterNo, setNewCounterNo] = useState("");
  const [cashTarget, setCashTarget] = useState(null);
  const [cashDirection, setCashDirection] = useState("in");
  const [cashAmount, setCashAmount] = useState("");
  const [cashReason, setCashReason] = useState("safe_float");
  const [cashNotes, setCashNotes] = useState("");

  const { data: cashReasonsData } = useQuery({
    queryKey: ["cash-movement-reasons"],
    queryFn: async () => {
      const { data: res } = await api.get("/counter-sessions/cash-movement-reasons");
      return res.reasons ?? [];
    },
  });
  const cashReasons = cashReasonsData ?? [];

  const { data, isLoading } = useQuery({
    queryKey: ["counters", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/counters", {
        params: branchId ? { branch_id: branchId } : {},
      });
      return res.counters;
    },
    enabled: Boolean(user),
  });

  const counters = data || [];

  const suggestedCounterNo = useMemo(() => {
    if (!counters.length) return 1;
    return Math.max(...counters.map((c) => c.counter_no)) + 1;
  }, [counters]);

  const createMutation = useMutation({
    mutationFn: async (payload) => {
      const { data: res } = await api.post("/counters", payload);
      return res;
    },
    onSuccess: async (res) => {
      toast.success(res.message || "Counter created.");
      queryClient.invalidateQueries({ queryKey: ["counters", branchId] });
      await refreshUser();
      setCreateOpen(false);
      setNewName("");
      setNewDeviceCode("");
      setNewCounterNo("");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Could not create counter.");
    },
  });

  const openMutation = useMutation({
    mutationFn: async ({ counterId, floatAmount }) => {
      const { data: res } = await api.post("/counter-sessions/open", {
        branch_id: branchId,
        counter_id: counterId,
        opening_float: floatAmount,
      });
      return res;
    },
    onSuccess: async (res) => {
      toast.success(res.message || "Shift opened.");
      queryClient.invalidateQueries({ queryKey: ["counters", branchId] });
      queryClient.invalidateQueries({ queryKey: ["counter-shift"] });
      await refreshUser();
      setOpenTarget(null);
      setOpeningFloat("0");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Could not open counter.");
    },
  });

  const cashMutation = useMutation({
    mutationFn: async ({ sessionId, direction, amount, reasonCode, notes }) => {
      const { data: res } = await api.post(`/counter-sessions/${sessionId}/cash-movements`, {
        branch_id: branchId,
        direction,
        amount,
        reason_code: reasonCode,
        notes: notes || undefined,
      });
      return res;
    },
    onSuccess: async (res) => {
      toast.success(res.message || "Cash movement recorded.");
      queryClient.invalidateQueries({ queryKey: ["counters", branchId] });
      queryClient.invalidateQueries({ queryKey: ["counter-shift"] });
      await refreshUser();
      setCashTarget(null);
      setCashAmount("");
      setCashNotes("");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Could not record cash movement.");
    },
  });

  const closeMutation = useMutation({
    mutationFn: async ({ sessionId, counted, notes }) => {
      const payload = { branch_id: branchId, close_notes: notes || undefined };
      if (counted !== "" && counted != null) {
        payload.closing_cash_counted = Number(counted);
      }
      const { data: res } = await api.post(`/counter-sessions/${sessionId}/close`, payload);
      return res;
    },
    onSuccess: async (res) => {
      const variance = res.summary?.variance;
      toast.success(
        variance != null
          ? `Shift closed. Cash variance: ${aed(variance)}`
          : res.message || "Shift closed.",
      );
      queryClient.invalidateQueries({ queryKey: ["counters", branchId] });
      queryClient.invalidateQueries({ queryKey: ["counter-shift"] });
      await refreshUser();
      setCloseTarget(null);
      setClosingCash("");
      setCloseNotes("");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Could not close counter.");
    },
  });

  function startOpen(counter) {
    setOpeningFloat("0");
    setOpenTarget(counter);
  }

  function startCash(counter, direction) {
    setCashTarget(counter);
    setCashDirection(direction);
    setCashAmount("");
    setCashReason(direction === "in" ? "safe_float" : "petty_cash");
    setCashNotes("");
  }

  function startClose(counter) {
    const expected = counter.session_summary?.expected_cash;
    setClosingCash(expected != null ? String(expected) : "");
    setCloseNotes("");
    setCloseTarget(counter);
  }

  return (
    <PageContainer>
      <PageHeader
        title={t("counter.managementTitle")}
        subtitle={t("counter.managementSubtitle", { branch: branchName })}
        actions={
          <Button
            size="sm"
            className="h-9 bg-[#2563EB] hover:bg-blue-700"
            data-testid="counter-new"
            disabled={!branchId || createMutation.isPending}
            onClick={() => {
              setNewCounterNo(String(suggestedCounterNo));
              setNewName(`Counter ${suggestedCounterNo}`);
              setNewDeviceCode(`POS-${String(suggestedCounterNo).padStart(2, "0")}`);
              setCreateOpen(true);
            }}
          >
            <Plus size={14} className="mr-1.5" /> {t("counter.newCounter")}
          </Button>
        }
      />

      <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50/80 px-4 py-3 flex gap-3 text-[12.5px] text-slate-700">
        <Info size={16} className="text-blue-600 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-slate-800">{t("counter.howItWorksTitle")}</div>
          <p className="mt-1 text-slate-600">{t("counter.howItWorks")}</p>
        </div>
      </div>

      {isLoading ? (
        <div className="h-32 grid place-items-center text-[13px] text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {counters.map((c) => {
            const isOpen = c.session_status === "open";
            const summary = c.session_summary;
            return (
              <Card key={c.id} className="p-4" data-testid={`counter-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[15px] font-semibold text-slate-900">{c.name}</div>
                    <div className="text-[11.5px] text-slate-500 font-mono">
                      {c.device_code || `CNT-${c.counter_no}`}
                    </div>
                  </div>
                  <Pill tone={isOpen ? "green" : c.is_active ? "amber" : "default"}>
                    {!c.is_active ? "Inactive" : isOpen ? t("counter.shiftOpen") : t("counter.shiftClosed")}
                  </Pill>
                </div>

                <div className="mt-3 space-y-1 text-[12.5px] text-slate-600">
                  <div className="flex justify-between">
                    <span>Counter No</span>
                    <span className="font-mono text-slate-800">{c.counter_no}</span>
                  </div>
                  {isOpen && c.session ? (
                    <>
                      <div className="flex justify-between">
                        <span>Cashier</span>
                        <span className="text-slate-800 font-medium">{c.session.opened_by_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Opening float</span>
                        <span className="font-mono">{format(c.session.opening_float)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Sales (session)</span>
                        <span className="font-mono font-semibold text-slate-900">
                          {format(summary?.net_sales ?? 0)}
                        </span>
                      </div>
                      {(summary?.cash_in > 0 || summary?.cash_out > 0) && (
                        <>
                          <div className="flex justify-between">
                            <span>Cash in (shift)</span>
                            <span className="font-mono text-emerald-700">{format(summary?.cash_in ?? 0)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Cash out (shift)</span>
                            <span className="font-mono text-red-700">{format(summary?.cash_out ?? 0)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between">
                        <span>Expected cash</span>
                        <span className="font-mono">{format(summary?.expected_cash ?? 0)}</span>
                      </div>
                      <div className="text-[11px] text-slate-400 pt-1">
                        Opened {formatOpenedAt(c.session.opened_at)}
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between">
                      <span>Branch</span>
                      <span className="text-slate-800">{c.branch_name}</span>
                    </div>
                  )}
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  {isOpen ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-[12px] text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`counter-close-${c.id}`}
                      disabled={!c.is_active || closeMutation.isPending}
                      onClick={() => startClose(c)}
                    >
                      <Lock size={12} className="mr-1.5" /> {t("counter.shiftCloseBtn")}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      className="h-8 text-[12px] bg-[#16A34A] hover:bg-emerald-700"
                      data-testid={`counter-open-${c.id}`}
                      disabled={!c.is_active || openMutation.isPending}
                      onClick={() => startOpen(c)}
                    >
                      <Unlock size={12} className="mr-1.5" /> {t("counter.shiftOpenBtn")}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-[12px]"
                    asChild
                  >
                    <Link to={`/reports/x-report?counter=${c.id}`}>
                      <FileText size={12} className="mr-1.5" /> X-Report
                    </Link>
                  </Button>
                </div>
                {isOpen && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-[12px] border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                      onClick={() => startCash(c, "in")}
                    >
                      {t("counter.cashIn")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-[12px] border-red-200 text-red-700 hover:bg-red-50"
                      onClick={() => startCash(c, "out")}
                    >
                      {t("counter.cashOut")}
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
          {!counters.length && (
            <div className="col-span-full text-[13px] text-slate-500 py-8 text-center">
              {t("counter.noCounters")}
            </div>
          )}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("counter.newCounter")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="new-counter-name">{t("counter.name")}</Label>
              <Input
                id="new-counter-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                data-testid="new-counter-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-counter-no">{t("counter.counterNo")}</Label>
              <Input
                id="new-counter-no"
                type="number"
                min="1"
                value={newCounterNo}
                onChange={(e) => setNewCounterNo(e.target.value)}
                data-testid="new-counter-no"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-device-code">{t("counter.deviceCode")}</Label>
              <Input
                id="new-device-code"
                value={newDeviceCode}
                onChange={(e) => setNewDeviceCode(e.target.value)}
                data-testid="new-counter-device"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              className="bg-[#2563EB] hover:bg-blue-700"
              disabled={!newName.trim() || !newCounterNo || createMutation.isPending}
              data-testid="new-counter-confirm"
              onClick={() =>
                createMutation.mutate({
                  branch_id: branchId,
                  name: newName.trim(),
                  counter_no: Number(newCounterNo),
                  device_code: newDeviceCode.trim() || null,
                })
              }
            >
              {createMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("counter.createCounter")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(cashTarget)} onOpenChange={(v) => !v && setCashTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {cashDirection === "in" ? t("counter.cashIn") : t("counter.cashOut")} — {cashTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="cash-amount">{t("counter.amount")}</Label>
              <Input
                id="cash-amount"
                type="number"
                min={moneyStep}
                step={moneyStep}
                value={cashAmount}
                onChange={(e) => setCashAmount(e.target.value)}
                data-testid="cash-movement-amount"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("counter.reason")}</Label>
              <Select value={cashReason} onValueChange={setCashReason}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {cashReasons.map((r) => (
                    <SelectItem key={r.code} value={r.code}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cash-notes">{t("counter.notes")}</Label>
              <Input
                id="cash-notes"
                value={cashNotes}
                onChange={(e) => setCashNotes(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCashTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              className={cashDirection === "in" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              variant={cashDirection === "out" ? "destructive" : "default"}
              disabled={!cashAmount || cashMutation.isPending}
              data-testid="cash-movement-confirm"
              onClick={() =>
                cashMutation.mutate({
                  sessionId: cashTarget.session.id,
                  direction: cashDirection,
                  amount: Number(cashAmount),
                  reasonCode: cashReason,
                  notes: cashNotes,
                })
              }
            >
              {cashMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(openTarget)} onOpenChange={(v) => !v && setOpenTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Open counter — {openTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="opening-float">Opening cash float </Label>
              <Input
                id="opening-float"
                type="number"
                min="0"
                step={moneyStep}
                value={openingFloat}
                onChange={(e) => setOpeningFloat(e.target.value)}
                data-testid="counter-opening-float"
              />
            </div>
            <p className="text-[12px] text-slate-500">
              Count the cash in the drawer before starting the shift. Sales will be tracked against this session.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenTarget(null)}>
              Cancel
            </Button>
            <Button
              className="bg-[#16A34A] hover:bg-emerald-700"
              disabled={openMutation.isPending}
              data-testid="counter-open-confirm"
              onClick={() =>
                openMutation.mutate({
                  counterId: openTarget.id,
                  floatAmount: Number(openingFloat) || 0,
                })
              }
            >
              {openMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Open shift"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(closeTarget)} onOpenChange={(v) => !v && setCloseTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Close counter — {closeTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[12.5px] space-y-1">
              <div className="flex justify-between">
                <span className="text-slate-500">Session sales</span>
                <span className="font-mono font-semibold">
                  {format(closeTarget?.session_summary?.net_sales ?? 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Expected cash in drawer</span>
                <span className="font-mono font-semibold">
                  {format(closeTarget?.session_summary?.expected_cash ?? 0)}
                </span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="closing-cash">Physical cash counted (optional)</Label>
              <Input
                id="closing-cash"
                type="number"
                min="0"
                step={moneyStep}
                value={closingCash}
                onChange={(e) => setClosingCash(e.target.value)}
                data-testid="counter-closing-cash"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="close-notes">Notes (optional)</Label>
              <Input
                id="close-notes"
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                placeholder="Variance reason, handover, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={closeMutation.isPending}
              data-testid="counter-close-confirm"
              onClick={() =>
                closeMutation.mutate({
                  sessionId: closeTarget.session.id,
                  counted: closingCash,
                  notes: closeNotes,
                })
              }
            >
              {closeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Close shift"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
